import React, { useState, useEffect } from 'react';
import { Plus, ArrowLeftRight, X } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/dateFormat';

const StockTransfer = () => {
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [parts, setParts] = useState([]);
  const [formData, setFormData] = useState({
    fromBranchId: '',
    toBranchId: '',
    items: [],
    notes: ''
  });

  useEffect(() => {
    fetchTransfers();
    fetchBranches();
    fetchParts();
  }, []);

  const fetchTransfers = async () => {
    try {
      const response = await axios.get('/api/stock-transfers');
      setTransfers(response.data.stockTransfers || []);
    } catch (error) {
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      setBranches(response.data.branches || response.data || []);
    } catch (error) {
    }
  };

  const fetchParts = async () => {
    try {
      const response = await axios.get('/api/parts');
      setParts(response.data.parts || response.data || []);
    } catch (error) {
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { partId: '', quantity: 1 }]
    });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    // Validate quantity against available stock
    if (field === 'quantity' || field === 'partId') {
      const partId = field === 'partId' ? value : updatedItems[index].partId;
      const quantity = field === 'quantity' ? value : updatedItems[index].quantity;
      const part = parts.find(p => p.id === partId);
      
      if (part && quantity > part.quantity) {
        toast.error(`Only ${part.quantity} units available for ${part.name} (${part.sku})`);
        if (field === 'quantity') {
          updatedItems[index][field] = part.quantity;
        }
      }
    }
    
    setFormData({ ...formData, items: updatedItems });
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    try {
      await axios.post('/api/stock-transfers', formData);
      toast.success('Stock transfer created successfully');
      setShowForm(false);
      setFormData({ fromBranchId: '', toBranchId: '', items: [], notes: '' });
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create stock transfer');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`/api/stock-transfers/${id}/status`, { status });
      toast.success('Status updated successfully');
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update status');
    }
  };

  const printDeliveryNote = (transfer) => {
    const printWindow = window.open('', '_blank');
    const items = Array.isArray(transfer.items) ? transfer.items : JSON.parse(transfer.items || '[]');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Delivery Note - ${transfer.transferNo}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .details { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .signature { margin-top: 50px; display: flex; justify-content: space-between; }
            .signature div { width: 200px; text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>STOCK TRANSFER DELIVERY NOTE</h2>
            <p>Transfer No: ${transfer.transferNo}</p>
          </div>
          <div class="details">
            <p><strong>From Branch:</strong> ${transfer.fromBranch?.name}</p>
            <p><strong>To Branch:</strong> ${transfer.toBranch?.name}</p>
            <p><strong>Date:</strong> ${formatDate(transfer.createdAt)}</p>
            <p><strong>Status:</strong> ${transfer.status}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>SKU</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => {
                const part = parts.find(p => p.id === item.partId);
                return `<tr>
                  <td>${part?.name || 'Unknown'}</td>
                  <td>${part?.sku || 'N/A'}</td>
                  <td>${item.quantity}</td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
          <div class="signature">
            <div>
              <p>_____________________</p>
              <p>Sender Signature</p>
              <p>Date: _______________</p>
            </div>
            <div>
              <p>_____________________</p>
              <p>Receiver Signature</p>
              <p>Date: _______________</p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Stock Transfer</h2>
        <button 
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Transfer
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold">Create Stock Transfer</h3>
              <button onClick={() => setShowForm(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">From Branch</label>
                  <select
                    value={formData.fromBranchId}
                    onChange={(e) => setFormData({ ...formData, fromBranchId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Branch</option>
                    {branches.map(branch => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">To Branch</label>
                  <select
                    value={formData.toBranchId}
                    onChange={(e) => setFormData({ ...formData, toBranchId: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="">Select Branch</option>
                    {branches.map(branch => (
                      <option key={branch.id} value={branch.id}>{branch.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Items</label>
                  <button type="button" onClick={addItem} className="text-blue-600 text-sm">
                    + Add Item
                  </button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <select
                      value={item.partId}
                      onChange={(e) => updateItem(index, 'partId', e.target.value)}
                      required
                      className="flex-1 px-3 py-2 border rounded-md"
                    >
                      <option value="">Select Part</option>
                      {parts.map(part => (
                        <option key={part.id} value={part.id}>
                          {part.name} ({part.sku}) - Stock: {part.quantity}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value))}
                      required
                      min="1"
                      max={parts.find(p => p.id === item.partId)?.quantity || 999}
                      className="w-20 px-3 py-2 border rounded-md"
                    />
                    <button type="button" onClick={() => removeItem(index)} className="text-red-600">
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-md">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md">
                  Create Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetails && selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold">Transfer Details - {selectedTransfer.transferNo}</h3>
              <button onClick={() => setShowDetails(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">From Branch</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.fromBranch?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">To Branch</label>
                  <p className="text-sm text-gray-900">{selectedTransfer.toBranch?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`px-2 py-1 rounded text-xs ${
                    selectedTransfer.status === 'APPROVED' || selectedTransfer.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                    selectedTransfer.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedTransfer.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedTransfer.createdAt)}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Part Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(Array.isArray(selectedTransfer.items) ? selectedTransfer.items : JSON.parse(selectedTransfer.items || '[]')).map((item, index) => {
                        const part = parts.find(p => p.id === item.partId);
                        return (
                          <tr key={index}>
                            <td className="px-4 py-2 text-sm text-gray-900">{part?.name || 'Unknown'}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{part?.sku || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {selectedTransfer.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedTransfer.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6">
          <h3 className="text-lg font-medium mb-4">Transfer History</h3>
          {transfers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Transfer No</th>
                    <th className="text-left py-2">From</th>
                    <th className="text-left py-2">To</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.map(transfer => (
                    <tr key={transfer.id} className="border-b">
                      <td className="py-2">{transfer.transferNo}</td>
                      <td className="py-2">{transfer.fromBranch?.name}</td>
                      <td className="py-2">{transfer.toBranch?.name}</td>
                      <td className="py-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          transfer.status === 'APPROVED' || transfer.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          transfer.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {transfer.status}
                        </span>
                      </td>
                      <td className="py-2">{formatDate(transfer.createdAt)}</td>
                      <td className="py-2">
                        <div className="flex space-x-2">
                          {transfer.status === 'PENDING' && (
                            <button
                              onClick={() => updateStatus(transfer.id, 'APPROVED')}
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                            >
                              Approve
                            </button>
                          )}
                          {transfer.status === 'APPROVED' && (
                            <button
                              onClick={() => updateStatus(transfer.id, 'COMPLETED')}
                              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                            >
                              Complete
                            </button>
                          )}
                          <button
                            onClick={() => { setSelectedTransfer(transfer); setShowDetails(true); }}
                            className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                          >
                            View
                          </button>
                          <button
                            onClick={() => printDeliveryNote(transfer)}
                            className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                          >
                            Print
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <ArrowLeftRight className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No stock transfers yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockTransfer;
